import axios from "axios";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

/**
 * Obtiene todas las categorías con sus subcategorías para un cliente específico
 * @param {string} clientId - ID del cliente
 * @returns {Promise<Object>} - Respuesta con las categorías y subcategorías
 */
export const getCategories = async (clientId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('categoriesLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(
      `${API_URL}/workflows/p2p/${clientId}/categories`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('categoriesLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('categoriesLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener categorías:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene una categoría específica con sus subcategorías
 * @param {string} clientId - ID del cliente
 * @param {string} categoryId - ID de la categoría
 * @returns {Promise<Object>} - Respuesta con la categoría y sus subcategorías
 */
export const getCategoryById = async (clientId, categoryId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('categoryLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(
      `${API_URL}/workflows/p2p/${clientId}/categories/${categoryId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('categoryLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('categoryLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener categoría:", error.response?.data || error);
    throw error;
  }
};

/**
 * Crea una nueva categoría, opcionalmente con un parent_id para subcategorías
 * @param {string} clientId - ID del cliente
 * @param {string} name - Nombre de la categoría
 * @param {string|null} parentId - ID de la categoría padre (null para categoría principal)
 * @param {string} description - Descripción de la categoría (opcional)
 * @returns {Promise<Object>} - Respuesta con la categoría creada
 */
export const createCategory = async (clientId, name, parentId = null, description = "") => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('categorySaving', { detail: true });
    window.dispatchEvent(event);

    const payload = { 
      name, 
      description,
      parentId: parentId
    };

    const response = await axios.post(
      `${API_URL}/workflows/p2p/${clientId}/categories`,
      payload
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('categorySaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('categorySaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al crear categoría:", error.response?.data || error);
    throw error;
  }
};

/**
 * Crea una nueva subcategoría
 * @param {string} clientId - ID del cliente
 * @param {string} name - Nombre de la subcategoría
 * @param {string} parentId - ID de la categoría padre
 * @param {string} description - Descripción de la subcategoría (opcional)
 * @returns {Promise<Object>} - Respuesta con la subcategoría creada
 */
export const createSubcategory = async (clientId, name, parentId, description = "") => {
  return createCategory(clientId, name, parentId, description);
};

/**
 * Actualiza una categoría existente y sus subcategorías
 * @param {string} clientId - ID del cliente
 * @param {string} categoryId - ID de la categoría
 * @param {Object} updateData - Datos a actualizar (name y/o subcategories)
 * @returns {Promise<Object>} - Respuesta con la categoría actualizada
 */
export const updateCategory = async (clientId, categoryId, updateData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('categoryUpdating', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.put(
      `${API_URL}/workflows/p2p/${clientId}/categories/${categoryId}`,
      updateData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('categoryUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('categoryUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al actualizar categoría:", error.response?.data || error);
    throw error;
  }
};

/**
 * Elimina una categoría y sus subcategorías asociadas
 * @param {string} clientId - ID del cliente
 * @param {string} categoryId - ID de la categoría
 * @returns {Promise<Object>} - Respuesta con la confirmación de eliminación
 */
export const deleteCategory = async (clientId, categoryId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('categoryDeleting', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.delete(
      `${API_URL}/workflows/p2p/${clientId}/categories/${categoryId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('categoryDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('categoryDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al eliminar categoría:", error.response?.data || error);
    throw error;
  }
};

/**
 * ========================
 * FUNCIONES PARA CENTROS DE COSTOS
 * ========================
 */

/**
 * Obtiene todos los centros de costos con información de categoría para un cliente específico
 * @param {string} clientId - ID del cliente
 * @returns {Promise<Object>} - Respuesta con los centros de costos
 */
export const getCostCenters = async (clientId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('costCentersLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(
      `${API_URL}/workflows/p2p/${clientId}/cost-centers`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('costCentersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('costCentersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener centros de costos:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene un centro de costos específico
 * @param {string} clientId - ID del cliente
 * @param {string} costCenterId - ID del centro de costos
 * @returns {Promise<Object>} - Respuesta con el centro de costos
 */
export const getCostCenterById = async (clientId, costCenterId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('costCenterLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(
      `${API_URL}/workflows/p2p/${clientId}/cost-centers/${costCenterId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('costCenterLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('costCenterLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener centro de costos:", error.response?.data || error);
    throw error;
  }
};

/**
 * Crea un nuevo centro de costos
 * @param {string} clientId - ID del cliente
 * @param {string} name - Nombre del centro de costos
 * @param {string} categoryId - ID de la categoría asociada
 * @returns {Promise<Object>} - Respuesta con el centro de costos creado
 */
export const createCostCenter = async (clientId, name, categoryId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('costCenterSaving', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.post(
      `${API_URL}/workflows/p2p/${clientId}/cost-centers`,
      { name, categoryId: categoryId }
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('costCenterSaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('costCenterSaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al crear centro de costos:", error.response?.data || error);
    throw error;
  }
};

/**
 * Actualiza un centro de costos existente
 * @param {string} clientId - ID del cliente
 * @param {string} costCenterId - ID del centro de costos
 * @param {Object} updateData - Datos a actualizar (name y/o category_id)
 * @returns {Promise<Object>} - Respuesta con el centro de costos actualizado
 */
export const updateCostCenter = async (clientId, costCenterId, updateData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('costCenterUpdating', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.put(
      `${API_URL}/workflows/p2p/${clientId}/cost-centers/${costCenterId}`,
      updateData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('costCenterUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('costCenterUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al actualizar centro de costos:", error.response?.data || error);
    throw error;
  }
};

/**
 * Elimina un centro de costos
 * @param {string} clientId - ID del cliente
 * @param {string} costCenterId - ID del centro de costos
 * @returns {Promise<Object>} - Respuesta con la confirmación de eliminación
 */
export const deleteCostCenter = async (clientId, costCenterId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('costCenterDeleting', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.delete(
      `${API_URL}/workflows/p2p/${clientId}/cost-centers/${costCenterId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('costCenterDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('costCenterDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al eliminar centro de costos:", error.response?.data || error);
    throw error;
  }
};

